import type {
  GameArmedOrderStatus,
  GameResources,
  TacticalPlannerResponse,
  TacticalPlannerStep,
} from "../types";

export const MAX_TACTICAL_PLANNER_STEPS = 50;

export function describeArmedOrderStatus(status: GameArmedOrderStatus): string {
  switch (status) {
    case "waiting_slot": return "En attente du slot global";
    case "waiting_resources": return "En attente des ressources";
    case "waiting_prerequisite": return "En attente d’un prérequis";
    case "ready": return "Prêt à être revérifié par le serveur";
    case "started": return "Démarré une fois";
    case "expired": return "Ordre conservé, auto-démarrage désactivé";
    case "stale_generation": return "Génération de partie obsolète";
    case "stale_rules": return "Règles serveur obsolètes";
    case "stale": return "État obsolète";
    case "obsolete": return "Cible obsolète";
    case "cancelled": return "Annulé";
  }
}

export function reorderTacticalPlannerSteps(
  steps: readonly TacticalPlannerStep[],
  index: number,
  offset: -1 | 1,
): TacticalPlannerStep[] {
  const target = index + offset;
  if (index < 0 || index >= steps.length || target < 0 || target >= steps.length) {
    return steps.map((step) => ({ ...step }));
  }
  const next = steps.map((step) => ({ ...step }));
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

export function removeTacticalPlannerStep(
  steps: readonly TacticalPlannerStep[],
  index: number,
): TacticalPlannerStep[] {
  return steps
    .filter((_step, stepIndex) => stepIndex !== index)
    .map((step) => ({ ...step }));
}

export function addTacticalPlannerStep(
  steps: readonly TacticalPlannerStep[],
  buildingKey: string,
  intentId: string,
): TacticalPlannerStep[] {
  if (steps.length >= MAX_TACTICAL_PLANNER_STEPS || !buildingKey || !intentId) {
    return steps.map((step) => ({ ...step }));
  }
  return [...steps.map((step) => ({ ...step })), { buildingKey, intentId }];
}

export function exportTacticalPlannerJson(response: TacticalPlannerResponse): string {
  return JSON.stringify(response, null, 2) + "\n";
}

function csvField(value: unknown): string {
  return `"${String(value ?? "").replaceAll('"', '""')}"`;
}

function resourceValue(value: GameResources, key: keyof GameResources): number {
  return Number.isFinite(value[key]) ? value[key] : 0;
}

export function exportTacticalPlannerCsv(response: TacticalPlannerResponse): string {
  const headers = [
    "intentId",
    "buildingKey",
    "fromLevel",
    "toLevel",
    "durationSeconds",
    "eta",
    "costBois",
    "costScorie",
    "costSang",
    "costIgnis",
    "deficitBois",
    "deficitScorie",
    "deficitSang",
    "deficitIgnis",
  ];
  const projectionByIntent = new Map(
    response.projection.steps.map((step) => [step.intentId, step]),
  );
  const rows = response.steps.map((step) => {
    const projection = projectionByIntent.get(step.intentId);
    const cost = projection?.cost ?? { bois: 0, scorie: 0, sang: 0, ignis: 0 };
    const deficits = projection?.deficits ?? { bois: 0, scorie: 0, sang: 0, ignis: 0 };
    return [
      step.intentId,
      step.buildingKey,
      projection?.fromLevel ?? "",
      projection?.toLevel ?? "",
      projection?.durationSeconds ?? "",
      projection?.eta ?? "",
      resourceValue(cost, "bois"),
      resourceValue(cost, "scorie"),
      resourceValue(cost, "sang"),
      resourceValue(cost, "ignis"),
      resourceValue(deficits, "bois"),
      resourceValue(deficits, "scorie"),
      resourceValue(deficits, "sang"),
      resourceValue(deficits, "ignis"),
    ].map(csvField).join(",");
  });
  return [headers.map(csvField).join(","), ...rows].join("\r\n") + "\r\n";
}

export function downloadTacticalPlannerFile(
  content: string,
  filename: string,
  mimeType: string,
): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.rel = "noopener";
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
